import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { getProductDetails, updateProductInventory } from "../../../api/catalog";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Tabs } from "../../primitives/Tabs";
import { cn } from "../../utils/cn";

type InventoryArticleDetailProps = {
  productId: string;
  onBack: () => void;
};

const TAB_IDS = ["stock", "historique", "action"] as const;

export function InventoryArticleDetail({ productId, onBack }: InventoryArticleDetailProps) {
  const [details, setDetails] = useState<Awaited<ReturnType<typeof getProductDetails>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<(typeof TAB_IDS)[number]>("stock");
  const [paramThreshold, setParamThreshold] = useState("");
  const [savingParam, setSavingParam] = useState(false);
  const [adjustmentQty, setAdjustmentQty] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProductDetails(productId)
      .then((data) => {
        if (!cancelled) {
          setDetails(data);
          setParamThreshold(data.inventory?.low_stock_threshold?.toString() ?? "");
        }
      })
      .catch(() => {
        if (!cancelled) setDetails(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [productId]);

  const saveThreshold = async () => {
    if (!productId) return;
    setSavingParam(true);
    try {
      await updateProductInventory(productId, {
        low_stock_threshold: paramThreshold === "" ? null : parseFloat(paramThreshold),
      });
      const data = await getProductDetails(productId);
      setDetails(data);
      setParamThreshold(data.inventory?.low_stock_threshold?.toString() ?? "");
    } finally {
      setSavingParam(false);
    }
  };

  const saveAdjustment = async () => {
    if (!productId || !details?.inventory) return;
    const qty = parseInt(adjustmentQty, 10);
    if (Number.isNaN(qty) || qty < 0) return;
    if (!adjustmentReason.trim()) {
      window.alert("Veuillez indiquer le motif de la correction (qui, quand, pourquoi).");
      return;
    }
    setSavingAdjustment(true);
    try {
      await updateProductInventory(productId, { stock_quantity: qty });
      const data = await getProductDetails(productId);
      setDetails(data);
      setAdjustmentQty("");
      setAdjustmentReason("");
      // TODO: envoyer le motif (adjustmentReason) au backend pour enregistrement dans l'historique (qui, quand, pourquoi)
    } finally {
      setSavingAdjustment(false);
    }
  };

  if (loading || !details) {
    return (
      <div className="p-6">
        <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm text-muted hover:text-text mb-4">
          <ArrowLeft size={16} /> Retour aux articles
        </button>
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  const inv = details.inventory;
  const available = inv ? Math.max(0, (inv.stock_quantity ?? 0) - (inv.reserved_quantity ?? 0)) : 0;
  const threshold = inv?.low_stock_threshold ?? null;
  const status = threshold !== null && available <= 0 ? "rupture" : threshold !== null && available <= threshold ? "faible" : "ok";
  const unit = details.product?.unit_kind || "unité";

  const tabs = [
    { id: "stock", label: "Stock" },
    { id: "historique", label: "Historique" },
    { id: "action", label: "Action" },
  ];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-4">
        <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm text-muted hover:text-text">
          <ArrowLeft size={16} /> Retour aux articles
        </button>
      </div>

      <h1 className="text-xl font-semibold text-text">
        {details.product?.name}
        {details.product?.code && <span className="text-muted font-normal ml-2">({details.product.code})</span>}
      </h1>

      <Tabs
        tabs={tabs}
        activeId={activeTab}
        onChange={(id) => setActiveTab(id as (typeof TAB_IDS)[number])}
      />

      {activeTab === "stock" && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-md border border-border bg-surface p-4">
              <p className="text-xs text-muted">Stock total</p>
              <p className="text-lg font-semibold text-text">{inv?.stock_quantity ?? 0} {unit}(s)</p>
            </div>
            <div className="rounded-md border border-border bg-surface p-4">
              <p className="text-xs text-muted">Réservé</p>
              <p className="text-lg font-semibold text-warning">{inv?.reserved_quantity ?? 0} {unit}(s)</p>
            </div>
            <div className="rounded-md border border-border bg-surface p-4">
              <p className="text-xs text-muted">Disponible</p>
              <p className={cn("text-lg font-semibold", available > 0 ? "text-success" : "text-danger")}>{available} {unit}(s)</p>
            </div>
            <div className="rounded-md border border-border bg-surface p-4">
              <p className="text-xs text-muted">Seuil minimum</p>
              <p className="text-lg font-semibold text-text">{threshold ?? "—"}</p>
            </div>
          </div>

          <div className="rounded-md border border-border bg-surface p-4 space-y-3">
            <p className="text-sm font-medium text-text">Seuil stock faible</p>
            <div className="flex flex-wrap items-end gap-2">
              <Input
                label="Seuil"
                placeholder="Optionnel"
                value={paramThreshold}
                onChange={(e) => setParamThreshold(e.target.value)}
              />
              <Button label={savingParam ? "Enregistrement..." : "Enregistrer"} intent="primary" size="sm" state={savingParam ? "loading" : "default"} onClick={saveThreshold} />
            </div>
          </div>

          <div className="rounded-md border border-border bg-surface p-4 space-y-3">
            <p className="text-sm font-medium text-text">Ajustement manuel</p>
            <p className="text-xs text-muted">Indiquez la quantité en stock après correction. La modification sera enregistrée dans l'historique (qui, quand, pourquoi).</p>
            <div className="grid gap-3 max-w-md">
              <Input
                label="Quantité après correction"
                placeholder="Ex. 50"
                value={adjustmentQty}
                onChange={(e) => setAdjustmentQty(e.target.value)}
              />
              <Input
                label="Motif (pourquoi) — obligatoire"
                placeholder="Raison de la correction"
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
              />
              <Button
                label={savingAdjustment ? "Enregistrement..." : "Enregistrer la correction"}
                intent="primary"
                size="sm"
                state={savingAdjustment ? "loading" : "default"}
                onClick={saveAdjustment}
              />
            </div>
          </div>

          <div className="rounded-md border border-border bg-surface-muted/50 p-4">
            <p className="text-sm font-medium text-text">Résumé commandes ouvertes</p>
            <p className="text-sm text-muted">À venir avec l'API commandes.</p>
          </div>
        </div>
      )}

      {activeTab === "historique" && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-text">Mouvements liés à cet article</p>
          <p className="text-sm text-muted">Mouvements (entrées, sorties, réservations, réceptions, corrections) : à venir avec l'API mouvements.</p>
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-muted">
                  <th className="text-left p-2 font-medium text-text">Date</th>
                  <th className="text-left p-2 font-medium text-text">Type</th>
                  <th className="text-right p-2 font-medium text-text">Quantité</th>
                  <th className="text-right p-2 font-medium text-text">Avant</th>
                  <th className="text-right p-2 font-medium text-text">Après</th>
                  <th className="text-left p-2 font-medium text-text">Source</th>
                  <th className="text-left p-2 font-medium text-text">Utilisateur</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={7} className="p-4 text-center text-muted">Aucun mouvement pour le moment.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "action" && (
        <p className="text-sm text-muted">Actions possibles sur cet article : à définir (commande fournisseur, réservation, etc.).</p>
      )}
    </div>
  );
}
